import { ContractEnums } from '@customer-mono/shared-types';
import { Box, ClickAwayListener, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { colors, CommonModal } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import { AutopayInfoWrapper } from '../../components/global-styles/GlobalStyles';
import { VerificationBannerContext } from '../../components/layout/dashboard-layout';
import CommonTable from '../../components/tableComponent/commonTableComponent';
import NoRecordsMessage from '../../components/ui-components/NoRecordsMessage';
import CommonStatusChip from '../../components/ui-components/StatusChipComponent';
import { cancelScheduledPayment } from '../../services/payment-services';
import { RootState } from '../../store';
import { hideLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { trackPaymentCancelled } from '../../utils/google-analytics';
import { formatCurrency, formatToUSDate, getScheduledPaymentCancelState } from '../../utils/helper';
import { paginateRows } from '../../utils/paymentHistoryHelpers';
import { useTranslation } from '../../utils/translations';

import { ScheduledPaymentMobileCard } from './components/ScheduledPaymentMobileCard';

interface ScheduledPaymentItem {
    Id?: string | number;
    DueDate?: string;
    PaymentDate?: string;
    Status?: string | number;
    isScheduledPayment?: boolean;
    UserId?: string;
    AspireContractId?: string;
    aspireContractId?: string;
    ContractId?: string | number;
    contractId?: string | number;
    Amount?: string | number;
    GrossAmount?: string | number | null;
    PlatformFee?: string | number | null;
    Platform?: string | number;
    ResponseMessage?: string | null;
    DisputedPayment?: boolean;
    ContractAspireId?: string;
}

interface SchedulePaymentProps {
    currentPage?: number;
    rowsPerPage?: number;
    onTotalCountChange?: (count: number) => void;
    scheduledPaymentsData?: ScheduledPaymentItem[];
    fetchScheduledPayments?: () => Promise<void>;
    isLoadingData?: boolean;
}

const StripeAmountCell: React.FC<{ amount: number; grossAmount?: number | null; platformFee?: number | null }> = ({
    amount,
    grossAmount,
    platformFee,
}) => {
    const [clickOpen, setClickOpen] = useState(false);
    const [hoverOpen, setHoverOpen] = useState(false);
    const isOpen = clickOpen || hoverOpen;

    const fee = platformFee != null && !isNaN(platformFee) ? Math.round(platformFee * 100) / 100 : null;
    const total = grossAmount != null && !isNaN(grossAmount) ? Math.round(grossAmount * 100) / 100 : null;

    const tooltipTitle = (
        <Box sx={{ p: 0.5, minWidth: 240 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5 }}>
                Credit card Payment Breakdown
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                <Typography variant="body2">Base Amount</Typography>
                <Typography variant="body2" fontWeight={600}>
                    {formatCurrency(amount)}
                </Typography>
            </Box>
            {fee != null && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography variant="body2">Processing Fee</Typography>
                    <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(fee)}
                    </Typography>
                </Box>
            )}
            {total != null && (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ borderTop: '1px solid rgba(255,255,255,0.25)', pt: 0.75, mt: 0.25 }}
                >
                    <Typography variant="body2" fontWeight={700}>
                        Total Charged
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                        {formatCurrency(total)}
                    </Typography>
                </Box>
            )}
        </Box>
    );

    return (
        <ClickAwayListener
            onClickAway={() => {
                setClickOpen(false);
                setHoverOpen(false);
            }}
        >
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.75}>
                <Typography variant="body2" fontWeight={700}>
                    {formatCurrency(total ?? amount)}
                </Typography>
                <Tooltip
                    open={isOpen}
                    title={tooltipTitle}
                    placement="top"
                    arrow
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    slotProps={{
                        tooltip: {
                            sx: {
                                bgcolor: 'rgba(40,40,40,0.95)',
                                color: 'white',
                                '& .MuiTooltip-arrow': { color: 'rgba(40,40,40,0.95)' },
                                maxWidth: 300,
                                borderRadius: 2,
                                p: 1.5,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                            },
                        },
                    }}
                >
                    <span
                        className="icon-info-circle"
                        role="button"
                        tabIndex={0}
                        style={{ fontSize: 16, color: colors.bluePreset[30], cursor: 'pointer' }}
                        onMouseEnter={() => setHoverOpen(true)}
                        onMouseLeave={() => setHoverOpen(false)}
                        onClick={() => setClickOpen(p => !p)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') setClickOpen(p => !p);
                        }}
                    />
                </Tooltip>
            </Box>
        </ClickAwayListener>
    );
};

const SchedulePayment: React.FC<SchedulePaymentProps> = ({
    currentPage = 0,
    rowsPerPage = 10,
    onTotalCountChange,
    scheduledPaymentsData: propScheduledPaymentsData = [],
    fetchScheduledPayments: propFetchScheduledPayments,
    isLoadingData = false,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showVerificationBanner = useContext(VerificationBannerContext);
    const loggedInUserId = useSelector((state: RootState) => state.user.userDetails?.Id);
    const isLoading = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);
    const [showCancelScheduledPaymentModal, setShowCancelScheduledPaymentModal] = useState(false);
    const [selectedInvoiceForCancel, setSelectedInvoiceForCancel] = useState<ScheduledPaymentItem | null>(null);
    const [isLoadingCutoffs] = useState(false);
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const isImpersonating = Boolean(impersonation?.isImpersonating);

    const handleCancelScheduledPayment = useCallback(
        (invoice: ScheduledPaymentItem) => {
            if (isImpersonating) return;
            const { disabled, toastMessage } = getScheduledPaymentCancelState(invoice.DueDate);
            if (disabled) {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: toastMessage,
                        severity: 'error',
                    }),
                );
                return;
            }
            setShowCancelScheduledPaymentModal(true);
            setSelectedInvoiceForCancel(invoice);
        },
        [isImpersonating, dispatch],
    );

    const confirmCancelScheduledPayment = async () => {
        if (isImpersonating) {
            setShowCancelScheduledPaymentModal(false);
            return;
        }
        try {
            if (!selectedInvoiceForCancel) return;
            const response = await cancelScheduledPayment(String(selectedInvoiceForCancel.Id));
            if (response.statusCode === 200) {
                // Track payment cancellation
                trackPaymentCancelled(
                    String(
                        selectedInvoiceForCancel.ContractAspireId ||
                            selectedInvoiceForCancel.ContractId ||
                            selectedInvoiceForCancel.Id,
                    ),
                );

                if (propFetchScheduledPayments) {
                    await propFetchScheduledPayments();
                }
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Scheduled Payment Cancelled Successfully',
                        severity: 'success',
                    }),
                );
            } else {
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: response.message || 'Failed to Cancel Scheduled Payment',
                        severity: 'error',
                    }),
                );
            }
        } catch (error) {
            console.error('Error canceling scheduled payment:', error);
            const err = error as { message?: string };
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: err?.message || 'Failed to Cancel Scheduled Payment',
                    severity: 'error',
                }),
            );
        } finally {
            dispatch(hideLoader());
        }
        setShowCancelScheduledPaymentModal(false);
    };

    const getScheduledPaymentStatus = (status: number) => {
        for (const [key, value] of Object.entries(ContractEnums.PaymentStatusTypes)) {
            if (value === status) {
                return key;
            }
        }
        return 'Unknown';
    };

    // Transform data to match CommonTable structure
    const transformedRows = useMemo(() => {
        if (!propScheduledPaymentsData || propScheduledPaymentsData.length === 0) return [];

        // Sort data by due/scheduled date (latest first)
        const sortedData = [...propScheduledPaymentsData].sort((a, b) => {
            const rawA = a.DueDate ?? a.PaymentDate;
            const rawB = b.DueDate ?? b.PaymentDate;
            const dateA = rawA ? new Date(rawA).getTime() : 0;
            const dateB = rawB ? new Date(rawB).getTime() : 0;
            return dateB - dateA;
        });

        return sortedData.map((invoice: ScheduledPaymentItem) => {
            // Handle both numeric and string status values
            let paymentStatus: string;
            if (typeof invoice.Status === 'string') {
                paymentStatus = invoice.Status;
            } else {
                paymentStatus = getScheduledPaymentStatus((invoice.Status as number) ?? 0);
            }

            // Normalize to lowercase for comparison
            const normalizedStatus = paymentStatus.toLowerCase();
            const isPending = normalizedStatus === 'pending';
            const isCancelled = normalizedStatus === 'cancelled';
            const isProcessed = normalizedStatus === 'processed';
            const isFailed = normalizedStatus === 'failed' || normalizedStatus === 'fail';
            const isDisputed = normalizedStatus === 'disputed';

            // Render status with appropriate colors
            let statusElement: React.ReactNode = paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1);
            let statusLabel = paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1);
            let statusColor: string = colors.greyPreset[100];
            if (isPending) {
                statusElement = (
                    <CommonStatusChip label="Pending" chipBgColor={colors.warningPreset[50]} sx={{ minWidth: 75 }} />
                );
                statusLabel = 'Pending';
                statusColor = colors.warningPreset[50];
            } else if (isCancelled) {
                statusElement = (
                    <CommonStatusChip label="Cancelled" chipBgColor={colors.errorPreset[10]} sx={{ minWidth: 75 }} />
                );
                statusLabel = 'Cancelled';
                statusColor = colors.errorPreset[10];
            } else if (isFailed) {
                statusElement = (
                    <CommonStatusChip label="Failed" chipBgColor={colors.errorPreset[10]} sx={{ minWidth: 75 }} />
                );
                statusLabel = 'Failed';
                statusColor = colors.errorPreset[10];
            } else if (isDisputed) {
                statusElement = (
                    <CommonStatusChip label="Disputed" chipBgColor={colors.errorPreset[10]} sx={{ minWidth: 75 }} />
                );
                statusLabel = 'Disputed';
                statusColor = colors.errorPreset[10];
            }
            const hasCancelButton =
                invoice.isScheduledPayment && (isPending || isCancelled) && invoice.UserId === loggedInUserId;

            // Past date or 7 PM EST cutoff from helper => disable cancel and set message/comment
            const cancelState = getScheduledPaymentCancelState(invoice.DueDate);
            const isPastCutoff = cancelState.disabled;

            // Comments: "-" when cancelled; when button shown but disabled, show reason (past date or same-day 7 PM); empty when Cancel allowed; "Scheduled by authorized user" or "-" otherwise
            const pastDateComment = 'Cancellation is not allowed as payment date is passed.';
            const sameDayCutoffComment =
                'Same-day payment cancellations are no longer available after 7:00 PM Eastern Time.';
            let comments: string = '';
            if (isCancelled) {
                console.log('came here 1');
                comments = '—';
            }
            if (invoice.ResponseMessage) {
                console.log('came here 2');

                comments = `${invoice.ResponseMessage} ${invoice.DisputedPayment ? 'Disputed' : ''}`;
            }
            if (hasCancelButton && !isCancelled && cancelState.disabled) {
                console.log('came here 3');

                comments = cancelState.isPastDate ? pastDateComment : sameDayCutoffComment;
            }
            if (hasCancelButton && !isCancelled) {
                console.log('came here 4');

                comments = '—'; // button visible and enabled => empty comments
            }
            if (invoice.UserId !== loggedInUserId) {
                console.log('came here 5');

                comments = t('payments.schedulePayment.commentScheduledByUser') || 'Scheduled by authorized user';
            }
            if (isProcessed) {
                comments = invoice.DisputedPayment
                    ? 'This payment has been disputed and is currently under review.'
                    : 'Payment is being processed. It may take 2-3 days to reflect in invoices.';
            }

            console.log(invoice, 'invoice', invoice.ResponseMessage, comments);

            const isStripePayment = invoice.Platform === ContractEnums.PaymentPlatformTypes.stripe;
            const paymentMode = isStripePayment ? 'Credit Card' : 'ACH';

            return {
                dueDate: (
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                            {invoice.DueDate ? formatToUSDate(invoice.DueDate) : 'N/A'}
                        </Typography>
                    </Box>
                ),
                dueDateISO: invoice.DueDate || '',
                dueDateFormatted: invoice.DueDate ? formatToUSDate(invoice.DueDate) : 'N/A',
                paymentMode: (
                    <CommonStatusChip
                        label={paymentMode}
                        chipBgColor={isStripePayment ? colors.bluePreset[30] : colors.successPreset[30]}
                        sx={{ minWidth: 90, fontWeight: 700 }}
                    />
                ),
                paymentModeCompact: (
                    <CommonStatusChip
                        label={paymentMode}
                        chipBgColor={isStripePayment ? colors.bluePreset[30] : colors.successPreset[30]}
                        sx={{
                            fontSize: '9px !important',
                            padding: '1px 6px !important',
                            borderRadius: '4px !important',
                            fontWeight: 600,
                            lineHeight: '1.4 !important',
                            minWidth: 'unset',
                        }}
                    />
                ),
                contractId: <Typography variant="body2">{invoice.AspireContractId || 'N/A'}</Typography>,
                amount: isStripePayment ? (
                    <StripeAmountCell
                        amount={Number(invoice.Amount)}
                        grossAmount={invoice.GrossAmount != null ? Number(invoice.GrossAmount) : null}
                        platformFee={invoice.PlatformFee != null ? Number(invoice.PlatformFee) : null}
                    />
                ) : (
                    formatCurrency(Number(invoice.Amount))
                ),
                amountCompact: formatCurrency(
                    invoice.GrossAmount != null ? Number(invoice.GrossAmount) : Number(invoice.Amount),
                ),
                status: statusElement,
                statusLabel,
                statusColor,
                hasCancelAction: Boolean(hasCancelButton && !isCancelled),
                originalInvoice: invoice,
                // Action column: only show Cancel button; disabled when past 7 PM EST on due date (from helper)
                actions:
                    hasCancelButton && !isCancelled ? (
                        <Box>
                            <Tooltip title={cancelState.tooltipMessage} placement="top" arrow>
                                <span>
                                    <ButtonComponent
                                        variant="contained"
                                        onClick={() => {
                                            if (isPending && !isPastCutoff) {
                                                handleCancelScheduledPayment(invoice);
                                            }
                                        }}
                                        MDButton={true}
                                        sx={{
                                            width: 'fit-content',
                                            pointerEvents: isImpersonating ? 'none' : 'auto',
                                            cursor: isImpersonating
                                                ? 'not-allowed'
                                                : isPastCutoff
                                                  ? 'not-allowed'
                                                  : 'pointer',
                                        }}
                                        SMButton={true}
                                        bgColor={colors.errorPreset[10]}
                                        disabled={isImpersonating || isPastCutoff}
                                    >
                                        {t('payments.upcomingPayments.Cancel') || 'Cancel'}
                                    </ButtonComponent>
                                </span>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            —
                        </Typography>
                    ),
                comments: comments ? (
                    <Typography variant="body2" color="textSecondary">
                        {comments}
                    </Typography>
                ) : (
                    ''
                ),
            };
        });
    }, [loggedInUserId, t, isImpersonating, handleCancelScheduledPayment, propScheduledPaymentsData]);

    useEffect(() => {
        if (onTotalCountChange) {
            onTotalCountChange(transformedRows.length);
        }
    }, [transformedRows.length, onTotalCountChange]);

    const paginatedRows = useMemo(
        () => paginateRows(transformedRows, currentPage, rowsPerPage),
        [transformedRows, currentPage, rowsPerPage],
    );

    // Column order: Contract ID, Scheduled date, Amount due, Status, Action, Comments
    const columns = [
        {
            id: 'contractId',
            label: t('payments.upcomingPayments.contractId') || 'Contract ID',
            sortable: false,
        },
        {
            id: 'dueDate',
            label: t('payments.schedulePayment.scheduledDate') || 'Scheduled date',
            sortable: true,
        },
        {
            id: 'paymentMode',
            label: 'Payment Mode',
            sortable: false,
        },
        {
            id: 'amount',
            label: t('payments.upcomingPayments.amount') || 'Amount Due',
            sortable: false,
            align: 'right' as const,
        },
        {
            id: 'status',
            label: t('payments.upcomingPayments.status') || 'Status',
            sortable: false,
            align: 'center' as const,
        },
        {
            id: 'actions',
            label: t('payments.schedulePayment.action') || 'Action',
            sortable: false,
            align: 'center' as const,
            minWidth: 70,
        },
        {
            id: 'comments',
            label: t('payments.schedulePayment.comments') || 'Comments',
            sortable: false,
            align: 'left' as const,
            minWidth: 320,
        },
    ];

    if (transformedRows.length === 0 && !isLoading && !isLoadingCutoffs && !isLoadingData) {
        return <NoRecordsMessage message={t('common.NoRecordsAvailable')} />;
    }

    if (isLoadingCutoffs || isLoadingData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={'calc(100dvh - 300px)'}>
                <Typography variant="h6" color="textSecondary" textAlign={'center'}>
                    {t('payments.schedulePayment.loadingData') || 'Loading data...'}
                </Typography>
            </Box>
        );
    }
    return (
        <Stack gap={3}>
            {isMobileOrTablet ? (
                <Stack px={{ xs: 1.5, sm: 3 }} gap={1.5}>
                    {paginatedRows.map((row, index) => (
                        <ScheduledPaymentMobileCard
                            key={String(row.originalInvoice?.Id ?? index)}
                            row={{
                                contractId: row.contractId,
                                dueDate: row.dueDateFormatted,
                                paymentMode: row.paymentMode,
                                paymentModeCompact: row.paymentModeCompact,
                                amount: row.amount,
                                amountCompact: row.amountCompact,
                                status: row.status,
                                statusLabel: row.statusLabel,
                                statusColor: row.statusColor,
                                comments: row.comments,
                                actions: row.actions,
                                hasCancelAction: row.hasCancelAction,
                            }}
                        />
                    ))}
                    <AutopayInfoWrapper direction="row" alignItems="center" sx={{ mt: 2 }}>
                        <span className="icon-info-filled" />
                        <Typography variant="subtitle2">{t('payments.schedulePayment.infoBanner')}</Typography>
                    </AutopayInfoWrapper>
                </Stack>
            ) : (
                <Stack px={3}>
                    <CommonTable
                        columns={columns}
                        rows={paginatedRows}
                        stickyHeader={true}
                        maxHeight={
                            showVerificationBanner
                                ? { xs: '100%', lg: 'calc(100dvh - 370px)' }
                                : { xs: '100%', lg: 'calc(100dvh - 332px)' }
                        }
                        sx={{
                            '& .MuiTable-root': {
                                tableLayout: 'fixed',
                                width: '100%',
                                minWidth: 1080,
                                '& .MuiTableHead-root .MuiTableCell-root, & .MuiTableBody-root .MuiTableCell-root': {
                                    boxSizing: 'border-box',
                                },
                                '& .MuiTableHead-root': {
                                    '& .MuiTableCell-root': {
                                        '&:last-of-type': {
                                            width: '23%',
                                        },
                                    },
                                },
                            },
                        }}
                    />
                    <AutopayInfoWrapper direction="row" alignItems="center" sx={{ mt: 2 }}>
                        <span className="icon-info-filled" />
                        <Typography variant="subtitle2">{t('payments.schedulePayment.infoBanner')}</Typography>
                    </AutopayInfoWrapper>
                </Stack>
            )}
            <CommonModal
                modalOpen={showCancelScheduledPaymentModal}
                onCloseModal={() => setShowCancelScheduledPaymentModal(false)}
                DialogMainTitle={t('payments.cancelScheduledPayment.title') || 'Cancel Scheduled Payment'}
                DialogSubTitle={
                    t('payments.cancelScheduledPayment.content') ||
                    'Are you sure you want to cancel this scheduled payment?'
                }
                primaryBtnName={t('payments.cancelScheduledPayment.cancel') || 'Cancel'}
                secondaryBtnName={t('payments.cancelScheduledPayment.confirm') || 'Confirm'}
                secondaryBtnClick={() => confirmCancelScheduledPayment()}
                primaryBtnClick={() => setShowCancelScheduledPaymentModal(false)}
            />
        </Stack>
    );
};

export default SchedulePayment;
